import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import {
  Upload, FileText, Users, Calendar, DollarSign, Building2,
  CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Loader2,
  Image, File as FileIcon, Table2, ScanLine, X, Edit3, AlertTriangle,
  Stethoscope, MapPin, Phone, Shield, BarChart3
} from 'lucide-react';
import { useAppMode } from '../contexts/AppModeContext';

const REVQ_LOGO = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/zakm44og_revQ_logo_black.png";
const ECHO_LOGO = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/r0ngtnwn_echo_logo_black.svg";
const LUMINA_LOGO = "https://customer-assets.emergentagent.com/job_revenue-cycle-1/artifacts/rms1wglh_lumina_logo_black.png";

const FILE_TYPES = {
  pdf: { icon: FileText, color: 'text-red-500 bg-red-50' },
  csv: { icon: Table2, color: 'text-emerald-500 bg-emerald-50' },
  xls: { icon: Table2, color: 'text-emerald-600 bg-emerald-50' },
  xlsx: { icon: Table2, color: 'text-emerald-600 bg-emerald-50' },
  edi: { icon: FileIcon, color: 'text-blue-500 bg-blue-50' },
  jpg: { icon: Image, color: 'text-amber-500 bg-amber-50' },
  jpeg: { icon: Image, color: 'text-amber-500 bg-amber-50' },
  png: { icon: Image, color: 'text-amber-500 bg-amber-50' },
};

const getFileInfo = (name) => {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  return FILE_TYPES[ext] || { icon: FileIcon, color: 'text-slate-500 bg-slate-50' };
};

// Simulate AI classification of files
const classifyFile = (file) => {
  const name = file.name.toLowerCase();
  if (name.includes('claim') || name.includes('837') || name.includes('billing') || name.includes('edi') || name.includes('payment') || name.includes('remittance'))
    return { app: 'revq', type: 'Claims / Billing Data' };
  if (name.includes('schedule') || name.includes('appointment') || name.includes('intake') || name.includes('roster'))
    return { app: 'echo', type: 'Schedule / Patient Flow' };
  if (name.includes('note') || name.includes('soap') || name.includes('chart') || name.includes('clinical') || name.includes('encounter'))
    return { app: 'lumina', type: 'Clinical Documentation' };
  if (name.includes('insurance') || name.includes('card') || name.includes('id'))
    return { app: 'echo', type: 'Insurance / ID Document' };
  if (name.includes('patient') || name.includes('demographic'))
    return { app: 'echo', type: 'Patient Demographics' };
  if (name.includes('provider') || name.includes('doctor'))
    return { app: 'lumina', type: 'Provider Data' };
  if (name.includes('report') || name.includes('analytics'))
    return { app: 'revq', type: 'Financial Report' };
  // Default: route based on extension
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'edi' || ext === 'x12') return { app: 'revq', type: 'EDI File' };
  if (ext === 'csv' || ext === 'xlsx') return { app: 'revq', type: 'Data Export' };
  if (ext === 'pdf') return { app: 'lumina', type: 'Document' };
  if (['jpg', 'jpeg', 'png'].includes(ext)) return { app: 'echo', type: 'Scanned Document' };
  return { app: 'echo', type: 'Unclassified' };
};

// Simulated AI extraction results
const DEMO_EXTRACTED = {
  practice: { name: 'Riverside Medical Group', npi: '1234567890', address: '2400 Riverside Dr, Suite 300', city: 'Austin', state: 'TX', zip: '78704', phone: '(512) 555-0190', specialty: 'Internal Medicine', confidence: 0.95 },
  providers: [
    { name: 'Dr. Sarah Chen', npi: '9876543210', specialty: 'Internal Medicine', confidence: 0.97 },
    { name: 'Dr. James Wilson', npi: '5678901234', specialty: 'Family Medicine', confidence: 0.93 },
    { name: 'Dr. Maria Lopez', npi: '3456789012', specialty: 'Pediatrics', confidence: 0.88 },
  ],
  patients: [
    { name: 'John Smith', mrn: 'MRN-0042891', dob: '03/15/1965', insurance: 'Blue Cross PPO' },
    { name: 'Maria Garcia', mrn: 'MRN-0038712', dob: '08/22/1978', insurance: 'Aetna HMO' },
    { name: 'Robert Johnson', mrn: 'MRN-0051204', dob: '11/30/1980', insurance: 'Medicare' },
    { name: 'Emily Davis', mrn: 'MRN-0029843', dob: '04/12/1970', insurance: 'United HC' },
    { name: 'James Taylor', mrn: 'MRN-0063117', dob: '06/08/1955', insurance: 'Medicare' },
    { name: 'Sarah Williams', mrn: 'MRN-0047283', dob: '01/22/1990', insurance: 'Cigna PPO' },
    { name: 'Michael Brown', mrn: 'MRN-0072495', dob: '09/14/1972', insurance: 'Aetna HMO' },
    { name: 'Jennifer Martinez', mrn: 'MRN-0083621', dob: '05/03/1985', insurance: 'Blue Cross PPO' },
  ],
  schedules: 24,
  claims: 47,
  payers: ['Blue Cross PPO', 'Aetna HMO', 'Medicare', 'United HC', 'Cigna PPO'],
};

const PROCESSING_STEPS = [
  { text: 'Analyzing uploaded files...', delay: 800 },
  { text: 'Classifying documents by type...', delay: 1200 },
  { text: 'Identifying practice information...', delay: 1000 },
  { text: 'Detecting providers and NPIs...', delay: 1100 },
  { text: 'Extracting patient records...', delay: 1400 },
  { text: 'Building payer directory...', delay: 900 },
  { text: 'Mapping schedules to Echo...', delay: 1000 },
  { text: 'Routing claims data to revQ...', delay: 1100 },
  { text: 'Preparing clinical context for Lumina...', delay: 900 },
  { text: 'Configuring your system...', delay: 800 },
];

// Demo file names for the "Try Demo" feature
const DEMO_FILES = [
  { name: 'claims_batch_837_jan2026.edi', size: 48200 },
  { name: 'patient_schedule_q1.csv', size: 12400 },
  { name: 'insurance_card_front_smith.jpg', size: 185000 },
  { name: 'clinical_notes_encounter_042.pdf', size: 67800 },
  { name: 'billing_remittance_835.edi', size: 31500 },
  { name: 'provider_roster_2026.xlsx', size: 24300 },
  { name: 'patient_demographics_export.csv', size: 8900 },
  { name: 'referral_letter_garcia.pdf', size: 52100 },
];

const createMockFile = ({ name, size }) => {
  const blob = new Blob([new ArrayBuffer(size)], { type: 'application/octet-stream' });
  return new File([blob], name, { lastModified: Date.now() });
};

// ── STEP 1: Upload ──
const UploadStep = ({ files, setFiles, onNext }) => {
  const [demoLoading, setDemoLoading] = useState(false);

  const handleTryDemo = () => {
    setDemoLoading(true);
    let i = 0;
    const addNext = () => {
      if (i >= DEMO_FILES.length) {
        setDemoLoading(false);
        return;
      }
      const mockFile = createMockFile(DEMO_FILES[i]);
      setFiles(prev => [...prev, mockFile]);
      i++;
      setTimeout(addNext, 180);
    };
    setFiles([]);
    setTimeout(addNext, 300);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="upload-step">
      <div className="text-center space-y-3">
        <h1 className="text-3xl font-bold text-slate-900">Upload your files</h1>
        <p className="text-base text-slate-500 max-w-xl mx-auto">
          Claims, schedules, patient records, insurance cards, clinical notes &mdash; AI will automatically classify each file and route it to the right app.
        </p>
        <p className="text-sm text-slate-400 max-w-lg mx-auto">
          Supports EDI 837/835 files, CSV exports, Excel spreadsheets, PDFs, scanned images, and more.
        </p>
      </div>

      {/* Demo display zone */}
      <div className="relative flex flex-col items-center justify-center py-16 px-8 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/30" data-testid="drop-zone">
        <div className="h-16 w-16 rounded-2xl bg-black flex items-center justify-center mb-4">
          <ScanLine className="h-7 w-7 text-white" />
        </div>
        <p className="text-lg font-semibold text-slate-800">Echo-Powered File Import</p>
        <p className="text-sm text-slate-500 mt-1">Use the demo below to see how it works</p>
        <div className="flex flex-wrap justify-center gap-2 mt-4">
          {['EDI 837', 'CSV', 'Excel', 'PDF', 'Screenshots', 'Schedules', 'Insurance Cards'].map(t => (
            <Badge key={t} variant="outline" className="text-[10px] text-slate-400 border-slate-200">{t}</Badge>
          ))}
        </div>
      </div>

      {/* Try Demo button */}
      {files.length === 0 && (
        <div className="text-center" data-testid="try-demo-section">
          <Button
            onClick={handleTryDemo}
            disabled={demoLoading}
            className="gap-2 text-sm bg-black hover:bg-gray-800 text-white px-8"
            data-testid="try-demo-btn"
          >
            {demoLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {demoLoading ? 'Loading sample files...' : 'Start Demo with Sample Files'}
          </Button>
        </div>
      )}

      {/* Disclaimers */}
      <div className="max-w-xl mx-auto space-y-2">
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 flex items-start gap-2.5">
          <Shield className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-700 leading-relaxed">Do not upload any personal information such as NPI, PHI, or other protected data.</p>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 flex items-start gap-2.5">
          <AlertTriangle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-700 leading-relaxed">This is a trial site. Features available on the live site once you sign up will be more comprehensive and robust.</p>
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2" data-testid="file-list">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">{files.length} file{files.length !== 1 ? 's' : ''} selected</p>
            <button onClick={() => setFiles([])} className="text-xs text-slate-500 hover:text-red-500">Clear all</button>
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1 rounded-xl border border-slate-200 p-2">
            {files.map((f, i) => {
              const info = getFileInfo(f.name);
              const Icon = info.icon;
              return (
                <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50">
                  <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", info.color)}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{f.name}</p>
                    <p className="text-[10px] text-slate-400">{(f.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Next */}
      <Button
        onClick={onNext}
        disabled={files.length === 0}
        className="w-full h-12 bg-black hover:bg-gray-800 text-white text-sm font-semibold gap-2"
        data-testid="upload-next-btn"
      >
        <Sparkles className="h-4 w-4" /> Analyze {files.length} File{files.length !== 1 ? 's' : ''} with AI
      </Button>
    </div>
  );
};

// ── STEP 2: Processing ──
const ProcessingStep = ({ files, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [classified, setClassified] = useState([]);

  useEffect(() => {
    // Classify files immediately
    const results = files.map(f => ({ file: f, ...classifyFile(f) }));
    setClassified(results);

    // Run through processing steps
    let step = 0;
    const advance = () => {
      if (step >= PROCESSING_STEPS.length) {
        setProgress(100);
        setTimeout(onComplete, 600);
        return;
      }
      setCurrentStep(step);
      setProgress(Math.round(((step + 1) / PROCESSING_STEPS.length) * 100));
      step++;
      setTimeout(advance, PROCESSING_STEPS[step - 1]?.delay || 1000);
    };
    const t = setTimeout(advance, 500);
    return () => clearTimeout(t);
  }, [files, onComplete]);

  const appCounts = { revq: 0, echo: 0, lumina: 0 };
  classified.forEach(c => { if (appCounts[c.app] !== undefined) appCounts[c.app]++; });

  return (
    <div className="max-w-2xl mx-auto space-y-8 py-8" data-testid="processing-step">
      <div className="text-center space-y-2">
        <div className="h-16 w-16 rounded-2xl bg-black flex items-center justify-center mx-auto mb-4 p-3">
          <img src="/ariaone_emblem_white.png" alt="AriaOne" className="h-full w-full object-contain" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">AI is setting up your system</h1>
        <p className="text-sm text-slate-500">Analyzing {files.length} files and configuring all apps</p>
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-black rounded-full transition-all duration-500" style={{ width: `${progress}%` }} data-testid="processing-progress" />
        </div>
        <p className="text-sm text-slate-600 text-center font-medium" data-testid="processing-status">
          {currentStep < PROCESSING_STEPS.length ? PROCESSING_STEPS[currentStep].text : 'Finalizing...'}
        </p>
      </div>

      {/* App routing summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { logo: REVQ_LOGO, name: 'revQ', count: appCounts.revq, color: 'border-emerald-200 bg-emerald-50/50' },
          { logo: ECHO_LOGO, name: 'Echo', count: appCounts.echo, color: 'border-blue-200 bg-blue-50/50' },
          { logo: LUMINA_LOGO, name: 'Lumina', count: appCounts.lumina, color: 'border-violet-200 bg-violet-50/50' },
        ].map(app => (
          <div key={app.name} className={cn("flex flex-col items-center gap-2 p-4 rounded-xl border", app.color)}>
            <img src={app.logo} alt={app.name} className="h-5 w-auto" />
            <p className="text-lg font-bold text-slate-800">{app.count}</p>
            <p className="text-[10px] text-slate-500">files routed</p>
          </div>
        ))}
      </div>

      {/* File-by-file status */}
      <div className="max-h-40 overflow-y-auto space-y-1 rounded-xl border border-slate-200 p-3">
        {classified.map((c, i) => {
          const info = getFileInfo(c.file.name);
          const Icon = info.icon;
          const appLabel = { revq: 'revQ', echo: 'Echo', lumina: 'Lumina' }[c.app] || c.app;
          const appColor = { revq: 'bg-emerald-100 text-emerald-700', echo: 'bg-blue-100 text-blue-700', lumina: 'bg-violet-100 text-violet-700' }[c.app];
          return (
            <div key={i} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
              <span className="text-xs text-slate-600 truncate flex-1">{c.file.name}</span>
              <Badge className={cn("text-[8px] px-1.5 py-0 border-0", appColor)}>{appLabel}</Badge>
              <span className="text-[9px] text-slate-400">{c.type}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── STEP 3: Review ──
const ReviewStep = ({ onComplete }) => {
  const data = DEMO_EXTRACTED;
  const [editingPractice, setEditingPractice] = useState(false);

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="review-step">
      <div className="text-center space-y-2">
        <div className="h-12 w-12 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto">
          <CheckCircle2 className="h-6 w-6 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Review What We Built</h1>
        <p className="text-sm text-slate-500">AI extracted and organized everything. Review and approve.</p>
      </div>

      {/* Practice Profile */}
      <Card data-testid="review-practice">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-600" />
              <h3 className="text-sm font-bold text-slate-800">Practice Profile</h3>
              <ConfidenceBadge confidence={data.practice.confidence} />
            </div>
            <button onClick={() => setEditingPractice(!editingPractice)} className="text-xs text-blue-600 font-medium flex items-center gap-1" data-testid="edit-practice-btn">
              <Edit3 className="h-3 w-3" /> {editingPractice ? 'Done' : 'Edit'}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Name', value: data.practice.name },
              { label: 'NPI', value: data.practice.npi },
              { label: 'Address', value: data.practice.address },
              { label: 'City / State / ZIP', value: `${data.practice.city}, ${data.practice.state} ${data.practice.zip}` },
              { label: 'Phone', value: data.practice.phone },
              { label: 'Specialty', value: data.practice.specialty },
            ].map(field => (
              <div key={field.label}>
                <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">{field.label}</p>
                {editingPractice ? (
                  <Input defaultValue={field.value} className="h-8 text-sm" />
                ) : (
                  <p className="text-sm text-slate-800 font-medium">{field.value}</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Providers */}
      <Card data-testid="review-providers">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Stethoscope className="h-4 w-4 text-slate-600" />
            <h3 className="text-sm font-bold text-slate-800">{data.providers.length} Providers Detected</h3>
          </div>
          <div className="space-y-2">
            {data.providers.map((p, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-50">
                <div className="h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center text-xs font-bold text-violet-600">
                  {p.name.split(' ').slice(-1)[0]?.[0]}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{p.name}</p>
                  <p className="text-[10px] text-slate-500">NPI: {p.npi} · {p.specialty}</p>
                </div>
                <ConfidenceBadge confidence={p.confidence} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Patients */}
      <Card data-testid="review-patients">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-slate-600" />
            <h3 className="text-sm font-bold text-slate-800">{data.patients.length} Patient Records Created</h3>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {data.patients.map((p, i) => (
              <div key={i} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-100">
                <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600">
                  {p.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-800 truncate">{p.name}</p>
                  <p className="text-[9px] text-slate-500">{p.mrn} · {p.insurance}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Schedules Built', value: data.schedules, icon: Calendar, color: 'text-blue-600 bg-blue-50' },
          { label: 'Claims Detected', value: data.claims, icon: DollarSign, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Payers Found', value: data.payers.length, icon: Shield, color: 'text-violet-600 bg-violet-50' },
          { label: 'Providers', value: data.providers.length, icon: Stethoscope, color: 'text-amber-600 bg-amber-50' },
        ].map(s => (
          <div key={s.label} className="flex flex-col items-center gap-1.5 p-4 rounded-xl border border-slate-200">
            <div className={cn("h-9 w-9 rounded-xl flex items-center justify-center", s.color)}>
              <s.icon className="h-4 w-4" />
            </div>
            <p className="text-xl font-bold text-slate-800">{s.value}</p>
            <p className="text-[10px] text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Approve */}
      <Button
        onClick={onComplete}
        className="w-full h-12 bg-black hover:bg-gray-800 text-white text-sm font-semibold gap-2"
        data-testid="approve-all-btn"
      >
        <CheckCircle2 className="h-4 w-4" /> Approve All & Continue
      </Button>
    </div>
  );
};

// ── STEP 4: Complete ──
const CompleteStep = () => {
  const navigate = useNavigate();
  const data = DEMO_EXTRACTED;

  return (
    <div className="max-w-2xl mx-auto text-center space-y-8 py-8" data-testid="complete-step">
      <div className="space-y-3">
        <div className="h-20 w-20 rounded-3xl bg-emerald-100 flex items-center justify-center mx-auto">
          <CheckCircle2 className="h-10 w-10 text-emerald-600" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900">Your system is ready</h1>
        <p className="text-base text-slate-500">AI configured all apps from your data in under a minute</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { value: data.patients.length, label: 'patients created' },
          { value: data.providers.length, label: 'providers detected' },
          { value: data.schedules, label: 'schedules built' },
        ].map(s => (
          <div key={s.label} className="p-5 rounded-xl border border-slate-200">
            <p className="text-3xl font-bold text-slate-900">{s.value}</p>
            <p className="text-xs text-slate-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* App status */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { logo: REVQ_LOGO, name: 'revQ', status: 'Claims-ready', href: '/revq', color: 'border-emerald-200' },
          { logo: ECHO_LOGO, name: 'Echo', status: 'Schedule built', href: '/echo', color: 'border-blue-200' },
          { logo: LUMINA_LOGO, name: 'Lumina', status: 'Context ready', href: '/lumina', color: 'border-violet-200' },
        ].map(app => (
          <button key={app.name} onClick={() => navigate(app.href)} className={cn("flex flex-col items-center gap-2 p-4 rounded-xl border-2 hover:shadow-md transition-all", app.color)} data-testid={`complete-${app.name.toLowerCase()}`}>
            <img src={app.logo} alt={app.name} className="h-5 w-auto" />
            <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
              <CheckCircle2 className="h-3 w-3" /> {app.status}
            </div>
          </button>
        ))}
      </div>

      {/* CTAs */}
      <div className="flex flex-col gap-3">
        <Button onClick={() => navigate('/landing')} className="h-12 bg-black hover:bg-gray-800 text-white font-semibold gap-2" data-testid="go-dashboard-btn">
          Go to Dashboard <ArrowRight className="h-4 w-4" />
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/revq')} className="flex-1 gap-1.5 text-xs">
            <DollarSign className="h-3.5 w-3.5" /> Start Billing
          </Button>
          <Button variant="outline" onClick={() => navigate('/echo')} className="flex-1 gap-1.5 text-xs">
            <Calendar className="h-3.5 w-3.5" /> View Schedule
          </Button>
          <Button variant="outline" onClick={() => navigate('/lumina')} className="flex-1 gap-1.5 text-xs">
            <FileText className="h-3.5 w-3.5" /> Write a Note
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Shared Components ──
const ConfidenceBadge = ({ confidence }) => (
  <Badge className={cn("text-[8px] px-1.5 py-0 border-0", confidence >= 0.9 ? 'bg-emerald-100 text-emerald-700' : confidence >= 0.8 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700')}>
    {Math.round(confidence * 100)}% match
  </Badge>
);

// ── MAIN ONBOARDING PAGE ──
const OnboardingPage = () => {
  const { isDemo } = useAppMode();
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);

  return (
    <div className="min-h-screen bg-white" data-testid="onboarding-page">
      {/* Header */}
      <header className="border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/ariaone_logo_black.png" alt="AriaOne" className="h-6 w-auto" />
          {step < 4 && (
            <div className="flex items-center gap-4">
              {[1, 2, 3].map(s => (
                <div key={s} className="flex items-center gap-2">
                  <div className={cn("h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors",
                    step > s ? "bg-emerald-100 text-emerald-600" : step === s ? "bg-black text-white" : "bg-slate-100 text-slate-400"
                  )}>
                    {step > s ? <CheckCircle2 className="h-3.5 w-3.5" /> : s}
                  </div>
                  <span className={cn("text-xs font-medium hidden sm:inline", step >= s ? "text-slate-700" : "text-slate-400")}>
                    {s === 1 ? 'Upload' : s === 2 ? 'Processing' : 'Review'}
                  </span>
                  {s < 3 && <div className={cn("w-8 h-px", step > s ? "bg-emerald-300" : "bg-slate-200")} />}
                </div>
              ))}
            </div>
          )}
          {step === 1 && (
            <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => window.history.back()} data-testid="skip-btn">
              Skip for now
            </Button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 py-10">
        {step === 1 && <UploadStep files={files} setFiles={setFiles} onNext={() => setStep(2)} />}
        {step === 2 && <ProcessingStep files={files} onComplete={() => setStep(3)} />}
        {step === 3 && <ReviewStep onComplete={() => setStep(4)} />}
        {step === 4 && <CompleteStep />}
      </main>
    </div>
  );
};

export default OnboardingPage;
